import { useContext, useEffect, useState } from 'react';
import { Pressable, ScrollView, Text, View } from 'react-native';
import { CheckBox } from 'react-native-web';
import ArrowLeftIcon from '@streamster/shared/assets/icons/arrow-left.svg';
import useIsMounted from '@streamster/shared/hooks/is-mounted-hook';

// context
import { ToasterContext } from '@streamster/shared/toaster/toaster.provider';
import { AuthContext } from '@streamster/shared/screens/auth/auth.utils';

// styles
import { useCouponTariffQuery } from '@streamster/shared/api/api.portal';
import Loader from '@streamster/shared/components/Loader/Loader';
import { useAppNavigation, useAppRoute } from '@streamster/shared/navigation/navigation.ref';
import React from 'react';
import mixins from '@streamster/shared/app/styles';
import Button from '@streamster/shared/components/Button/Button';
import { MaterialInput } from '../../components/MaterialInput/MaterialInput';
import { useMobileWebAppStore } from '../../components/WebNavigation/mobileAppWeb.store';
import handleLink from '@streamster/shared/helpers/handle-link';
import { getAppName, useIsMobile } from '@streamster/shared/utils';
import styles from '@streamster/shared/screens/register/register.styles';

const RegisterScreen = () => {
  const isMounted = useIsMounted();
  const isMobile = useIsMobile();
  const navigation = useAppNavigation();
  const route = useAppRoute<'Register' | 'RegisterCoupon'>();
  const isCouponRegister = route.name === 'RegisterCoupon';
  const [isReferralCodeVisible, setReferralCodeVisibility] = useState(
    Boolean(route?.params?.referralCode),
  );
  const { isMobileWebApp, setMobileWebApp } = useMobileWebAppStore();
  const [progress, setProgress] = useState(false);
  const { register } = useContext(AuthContext);
  const { error } = useContext(ToasterContext);
  const [fields, setFields] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    email: '',
    couponCode: '',
    referralCode: route?.params?.referralCode || '',
    terms: false,
  });
  const { data: tariff, isPending } = useCouponTariffQuery(fields.couponCode, {
    enabled: Boolean(isCouponRegister && fields.couponCode),
  });

  useEffect(() => {
    if (route.params?.mobileApp && !isMobileWebApp) {
      setMobileWebApp(true);
    }
  }, [route?.params?.mobileApp]);

  const setFieldValue = (field: keyof typeof fields) => (value: string | boolean | number) => {
    setFields(f => ({ ...f, [field]: value }));
  };

  const requiredFields: Array<keyof typeof fields> = [
    'email',
    'password',
    'username',
    'terms',
    'confirmPassword',
  ];
  const isFormValid =
    requiredFields.every(field => (typeof field === 'string' ? field.trim().length > 0 : field)) &&
    fields.password === fields.confirmPassword;

  const handleRegister = async () => {
    const { username, password, email, confirmPassword, terms, couponCode, referralCode } = fields;

    if (password !== confirmPassword) {
      return error({ message: "Passwords don't match" });
    }
    if (!terms) {
      return error({ message: 'Please accept our terms and conditions' });
    }
    if (!isFormValid) {
      return error({ message: 'Please fill all fields' });
    }
    if (isCouponRegister && !couponCode) {
      return error({ message: 'Please enter coupon code to use this promotion' });
    }

    try {
      setProgress(true);
      await register({ login: username, password, email, couponCode, referralCode });
      navigation.navigate('SignIn', { handler: 'after-registration' });
    } catch (e: any) {
      error({ message: e.message });
    } finally {
      if (isMounted()) {
        setProgress(false);
      }
    }
  };

  return (
    <ScrollView contentContainerStyle={{ flexGrow: 1 }}>
      <View style={styles.webWrapper}>
        <View style={styles.box}>
          {!isMobileWebApp ? (
            <ArrowLeftIcon
              onClick={() => navigation.navigate('SignIn')}
              style={styles.goBackIcon}
            />
          ) : null}
          <Text style={[styles.webHeader, isMobile && mixins().styles.margin.topI6]}>
            Create account
          </Text>
          <Text style={styles.webSubHeader}>to continue to {getAppName()}</Text>
          <View style={[styles.inputContainer, styles.webInputContainer]}>
            <MaterialInput
              onChangeText={setFieldValue('username')}
              value={fields.username}
              autoComplete="username"
              label="User name"
            />
            <MaterialInput
              onChangeText={setFieldValue('email')}
              value={fields.email}
              autoComplete="email"
              label="Email"
            />
            <MaterialInput
              secureTextEntry
              onChangeText={setFieldValue('password')}
              value={fields.password}
              autoComplete="password"
              label="Password"
            />
            <MaterialInput
              secureTextEntry
              onChangeText={setFieldValue('confirmPassword')}
              value={fields.confirmPassword}
              autoComplete="password"
              label="Confirm password"
            />
            {isReferralCodeVisible ? (
              <MaterialInput
                onChangeText={setFieldValue('referralCode')}
                value={fields.referralCode}
                label="Referral code"
              />
            ) : (
              <Pressable
                onPress={() => {
                  setReferralCodeVisibility(true);
                }}
              >
                <Text style={styles.haveReferralCodeText}>Have a referral code?</Text>
              </Pressable>
            )}
            {isCouponRegister && (
              <>
                <MaterialInput
                  onChangeText={setFieldValue('couponCode')}
                  value={fields.couponCode}
                  label="Coupon code"
                />
                {fields.couponCode && (
                  <View style={styles.tariffBox}>
                    {isPending && <Loader size={30} />}
                    {tariff && (
                      <Text style={styles.tariffText}>
                        Coupon code valid for pricing plan:
                        <br /> <Text style={styles.tariffName}>{tariff.name}</Text>
                      </Text>
                    )}
                    {!isPending && !tariff && (
                      <Text style={[styles.tariffText, styles.tariffError]}>
                        Coupon code is invalid or already used
                      </Text>
                    )}
                  </View>
                )}
              </>
            )}
            <View style={styles.termsWrapper}>
              <CheckBox
                style={styles.checkbox}
                color={mixins().color.blue}
                value={fields.terms}
                onValueChange={setFieldValue('terms')}
              />
              <Text style={styles.termsText} onPress={() => setFieldValue('terms')(!fields.terms)}>
                I agree to{' '}
                <Text
                  style={styles.termsLink}
                  onPress={() => handleLink('https://streamster.io/terms-conditions/')}
                >
                  Terms & Conditions
                </Text>
              </Text>
            </View>
            <Button onPress={handleRegister} loading={progress} style={styles.btnRegister}>
              Create
            </Button>
          </View>
        </View>
      </View>
    </ScrollView>
  );
};

export default RegisterScreen;
